import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { compose } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { Popover } from 'react-tiny-popover';
import PropTypes from 'prop-types';

import Button from '../../../../components/Button';
import Input from '../../../../components/Form/Input';
import IMG_WHY_DO_WE_NEED from '../../../../assets/images/why_do_we_need.svg';
import { nextAction } from '../../../../saga/actions/workflow';
import { getHTTPErrorMessage } from '../../../../utils/fetch';
import { maskDateOfBirth, unmaskNumber, maskSSN1, maskSSN2, maskSSN3, matchSSN1, matchSSN2, matchSSN3, unmaskSSN } from '../../../../utils/masks';
import { getNextUrl } from '../url';

const getDob = (dob) => {
  if (!dob) return '';
  return dob.replace(/[-]+/g, '/');
};

const getSSN = (ssn, index) => {
  if (!ssn) return '';
  if (ssn.length !== 9) return '';
  switch (index) {
    case 0: return ssn.substring(0, 3);
    case 1: return ssn.substring(3, 2);
    case 2: return ssn.substring(5, 9);
    default: return '';
  }
};

const Step5 = (props) => {
  const { workflow } = props;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // get functions to build form with useForm() hook
  const hookForm = useForm({ reValidateMode: 'onSubmit' });
  const { handleSubmit, setValue } = hookForm;

  // set initial values
  const dateOfBirth = useMemo(() => getDob(get(props.workflow, 'data.data.dob')), [workflow]);
  const SSN1 = useMemo(() => getSSN(get(props.workflow, 'data.data.ssn'), 0), [workflow]);
  const SSN2 = useMemo(() => getSSN(get(props.workflow, 'data.data.ssn'), 1), [workflow]);
  const SSN3 = useMemo(() => getSSN(get(props.workflow, 'data.data.ssn'), 2), [workflow]);
  const errorMsg = useMemo(() => getHTTPErrorMessage(workflow.error), [workflow]);

  useEffect(() => {
    setValue('dateOfBirth', dateOfBirth);
    setValue('SSN1', SSN1);
    setValue('SSN2', SSN2);
    setValue('SSN3', SSN3);
  }, [dateOfBirth, SSN1, SSN2, SSN3, setValue]);

  // handle functions
  const onSubmit = (data) => {
    props.nextAction({
      url: getNextUrl(),
      method: 'PUT',
      data: {
        Ssn: `${data.SSN1}${data.SSN2}${data.SSN3}`,
        Dob: moment(data.dateOfBirth, 'MM/DD/YYYY').format('YYYY-MM-DD'),
      },
    });
    return false;
  };

  const validateDate = (val) => {
    const realValue = unmaskNumber(val);
    if (!realValue) {
      return 'Required';
    }

    if (realValue.length < 8) {
      return 'Date of Birth is not valid';
    }

    if (!moment(val, 'MM/DD/YYYY').isValid()) {
      return 'Date of Birth is not valid';
    }

    if (moment().diff(moment(val, 'MM-DD-YYYY'), 'years') < 18) {
      return 'Must be over 18 years old';
    }

    if (parseInt(val.slice(-4), 10) < 1900) {
      return 'Please confirm date of birth';
    }
    return null;
  };

  const handleDateChange = (e) => {
    const changedValue = e.target.value;
    const realValue = unmaskNumber(changedValue);
    if (realValue.length >= 8) {
      document.getElementById('SSN1').focus();
    }
  };

  const validateSSN = (name, val) => {
    const realValue = unmaskSSN(val);
    let match = null;
    switch (name) {
      case 'SSN1':
        match = matchSSN1;
        break;
      case 'SSN2':
        match = matchSSN2;
        break;
      case 'SSN3':
        match = matchSSN3;
        break;
      default:
        match = matchSSN1;
        break;
    }
    if (!realValue) {
      return 'Required';
    } if (!match.test(realValue)) {
      return 'Invalid SSN';
    }
    return null;
  };

  const handleSSNChange = (name, e) => {
    const changedValue = e.target.value;
    const realValue = unmaskSSN(changedValue);
    switch (name) {
      case 'SSN1':
        if (realValue.length >= 3) {
          document.getElementById('SSN2').focus();
        }
        break;
      case 'SSN2':
        if (realValue.length >= 2) {
          document.getElementById('SSN3').focus();
        }
        break;
      default:
        break;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center md:text-left font-bold text-[26px] leading-[26px] md:text-[32px] md:leading-[38px] text-[#263952]">
        We need some
        {' '}
        <br />
        {' '}
        personal details from you.
      </div>
      <div className="text-center md:text-left font-normal text-[15px] leading-[20px] text-[#263952] mt-[8px] ">
        This won’t affect your credit score
        {' '}
        <sup>1</sup>
      </div>
      <div className="text-center md:text-left font-normal text-[14px] leading-[20px] text-[#9B9B9B] mt-[15px] flex">
        <img src={IMG_WHY_DO_WE_NEED} alt="why" />
        <Popover
          isOpen={isPopoverOpen}
          positions={['top']} // preferred positions by priority
          align="start"
          padding={2}
          onClickOutside={() => setIsPopoverOpen(false)}
          content={(
            <>
              <div className="rounded-md border border-[#00214732] p-5 bg-[#ffffff] m-auto max-w-[90%] md:max-w-[400px] max-h-[500px] overflow-y-auto text-[#3f556e] text-[14px] scrollbar">
                Your date of birth and Social Security Number are needed to confirm your identity.
                <br />
                <br />
                Providing your Social Security Number will NOT affect your credit score.
              </div>
              <span className="arrow left-2 hidden md:block" />
            </>
          )}
        >
          <span className="pl-2 underline cursor-pointer" onClick={() => setIsPopoverOpen(!isPopoverOpen)}>Why do we need this?</span>
        </Popover>
      </div>
      <div className="text-center md:text-left font-medium text-[18px] leading-[32px] text-black mt-[30px]">
        What’s your date of birth and Social Security Number?
      </div>
      <div className="w-full">
        <Input
          label="Date of Birth"
          name="dateOfBirth"
          className="w-[200px]"
          isMasked={maskDateOfBirth}
          placeHolder="__/__/____"
          hookForm={hookForm}
          onCustomChange={handleDateChange}
          validate={validateDate}
        />
      </div>
      <div className="mt-5 text-left font-bold text-[14px] leading-[16px] text-black">
        Social Security Number
      </div>
      <div className="flex">
        <Input
          name="SSN1"
          className="mt-0 w-[70px]"
          label=""
          isMasked={maskSSN1}
          hookForm={hookForm}
          onCustomChange={handleSSNChange.bind(this, 'SSN1')}
          validate={validateSSN.bind(this, 'SSN1')}
        />
        <Input
          name="SSN2"
          className="mt-0 w-[50px] ml-[10px]"
          label=""
          isMasked={maskSSN2}
          hookForm={hookForm}
          onCustomChange={handleSSNChange.bind(this, 'SSN2')}
          validate={validateSSN.bind(this, 'SSN2')}
        />
        <Input
          name="SSN3"
          className="mt-0 w-[100px] ml-[10px]"
          label=""
          isMasked={maskSSN3}
          hookForm={hookForm}
          onCustomChange={handleSSNChange.bind(this, 'SSN3')}
          validate={validateSSN.bind(this, 'SSN3')}
        />
      </div>
      <Button isLoading={workflow.isLoading} type="submit" className="w-full md:w-[240px]">Continue</Button>
      {errorMsg && <div className="mt-3 mb-3 text-[#d71f28] text-xs">{errorMsg}</div>}
    </form>
  );
};

Step5.propTypes = {
  workflow: PropTypes.object.isRequired,
  nextAction: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => ({
      workflow: state.workflow,
    }),
    {
      nextAction,
    }
  )
)(Step5);
